import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import ComplianceStatusOverview from './components/ComplianceStatusOverview';
import ChemicalUsageTracking from './components/ChemicalUsageTracking';
import CriticalAlertsPanel from './components/CriticalAlertsPanel';
import RecentActivityFeed from './components/RecentActivityFeed';
import DocumentManagement from './components/DocumentManagement';
import ComplianceDataTable from './components/ComplianceDataTable';
import CommentThread from './components/CommentThread';
import Icon from '../../components/AppIcon';
import Select from '../../components/ui/Select';
import Button from '../../components/ui/Button';

const ComplianceDashboard = () => {
  const [regulationType, setRegulationType] = useState('all');
  const [compliancePeriod, setCompliancePeriod] = useState('current-season');
  const [autoReportEnabled, setAutoReportEnabled] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const regulationOptions = [
    { value: 'all', label: 'All Regulations' },
    { value: 'epa', label: 'EPA Regulations' },
    { value: 'osha', label: 'OSHA Safety' },
    { value: 'organic', label: 'Organic Certification' },
    { value: 'state', label: 'State Licensing' }
  ];

  const periodOptions = [
    { value: 'current-season', label: 'Current Season' },
    { value: 'last-30-days', label: 'Last 30 Days' },
    { value: 'last-90-days', label: 'Last 90 Days' },
    { value: 'year-to-date', label: 'Year to Date' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const complianceMetrics = [
    {
      id: 1,
      title: 'Overall Compliance',
      value: '84%',
      change: '+3%',
      trend: 'up',
      icon: 'Shield',
      color: 'text-success',
      bgColor: 'bg-success/10'
    },
    {
      id: 2,
      title: 'Active Violations',
      value: '3',
      change: '-2',
      trend: 'down',
      icon: 'AlertTriangle',
      color: 'text-warning',
      bgColor: 'bg-warning/10'
    },
    {
      id: 3,
      title: 'Pending Inspections',
      value: '5',
      change: '+1',
      trend: 'up',
      icon: 'FileSearch',
      color: 'text-accent',
      bgColor: 'bg-accent/10'
    },
    {
      id: 4,
      title: 'Documents Expiring',
      value: '7',
      change: '0',
      trend: 'neutral',
      icon: 'Clock',
      color: 'text-error',
      bgColor: 'bg-error/10'
    }
  ];

  const handleGenerateReport = () => {
    console.log('Generating compliance report...');
    alert('Compliance report generation started. You will receive an email when ready.');
  };

  const handleScheduleReport = () => {
    console.log('Scheduling automated reports...');
    alert('Automated report scheduling configured successfully.');
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'documents', label: 'Documents', icon: 'FileText' },
    { id: 'chemicals', label: 'Chemical Tracking', icon: 'Droplets' },
    { id: 'data', label: 'Data Table', icon: 'Table' },
    { id: 'comments', label: 'Comments', icon: 'MessageSquare' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">Compliance Dashboard</h1>
              <p className="text-sm sm:text-base text-gray-600 mt-2">Track regulatory compliance status and manage documentation</p>
            </div>
            
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
              <Select
                options={regulationOptions}
                value={regulationType}
                onChange={setRegulationType}
                className="w-full sm:w-48"
              />
              <Select
                options={periodOptions}
                value={compliancePeriod}
                onChange={setCompliancePeriod}
                className="w-full sm:w-48"
              />
              <Button
                onClick={handleGenerateReport}
                iconName="FileDown"
                iconPosition="left"
                className="w-full sm:w-auto"
              >
                Generate Report
              </Button>
            </div>
          </div>
        </div>

        {/* Metrics Overview */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {complianceMetrics?.map((metric) => (
            <div key={metric?.id} className="bg-white rounded-lg border border-gray-200 p-6 agricultural-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-lg ${metric?.bgColor}`}>
                  <Icon name={metric?.icon} size={24} className={metric?.color} />
                </div>
                <div className={`flex items-center space-x-1 text-sm ${
                  metric?.trend === 'up' ? 'text-success' : 
                  metric?.trend === 'down'? 'text-error' : 'text-gray-500'
                }`}>
                  <Icon 
                    name={metric?.trend === 'up' ? 'TrendingUp' : metric?.trend === 'down' ? 'TrendingDown' : 'Minus'} 
                    size={16} 
                  />
                  <span className="font-medium">{metric?.change}</span>
                </div>
              </div>
              <h3 className="text-sm text-gray-600 mb-1">{metric?.title}</h3>
              <p className="text-2xl font-bold text-gray-900">{metric?.value}</p>
            </div>
          ))}
        </div>

        {/* Critical Alerts */}
        <CriticalAlertsPanel />

        {/* Tab Navigation */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 overflow-x-auto">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`
                    whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2
                    ${activeTab === tab?.id
                      ? 'border-primary text-primary' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <Icon name={tab?.icon} size={18} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            <div className="lg:col-span-2 space-y-4 sm:space-y-6">
              <ComplianceStatusOverview />
              <ChemicalUsageTracking />
            </div>
            <div className="lg:col-span-1 space-y-4 sm:space-y-6">
              <RecentActivityFeed />
              
              {/* Quick Actions */}
              <div className="bg-white rounded-lg border border-gray-200 p-6 agricultural-shadow">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    iconName="FileDown"
                    iconPosition="left"
                    onClick={handleGenerateReport}
                  >
                    Generate Compliance Report
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    iconName="Calendar"
                    iconPosition="left"
                    onClick={handleScheduleReport}
                  >
                    Schedule Automated Reports
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    iconName="Upload"
                    iconPosition="left"
                  >
                    Upload Document
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    iconName="Bell"
                    iconPosition="left"
                  >
                    Configure Alerts
                  </Button>
                </div>
              </div>

              {/* Compliance Score Breakdown */}
              <div className="bg-white rounded-lg border border-gray-200 p-6 agricultural-shadow">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Compliance Score Breakdown</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600">EPA Regulations</span>
                      <span className="font-medium text-success">92%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-success h-2 rounded-full" style={{ width: '92%' }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600">OSHA Safety</span>
                      <span className="font-medium text-warning">78%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-warning h-2 rounded-full" style={{ width: '78%' }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600">Organic Certification</span>
                      <span className="font-medium text-success">100%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-success h-2 rounded-full" style={{ width: '100%' }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600">State Licensing</span>
                      <span className="font-medium text-error">65%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-error h-2 rounded-full" style={{ width: '65%' }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="space-y-6">
            <DocumentManagement />
          </div>
        )}

        {activeTab === 'chemicals' && (
          <div className="space-y-6">
            <ChemicalUsageTracking />
          </div>
        )}

        {activeTab === 'data' && (
          <div className="space-y-6">
            <ComplianceDataTable />
          </div>
        )}

        {activeTab === 'comments' && (
          <div className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <div className="flex items-start gap-3">
                <Icon name="Info" className="w-5 h-5 text-accent mt-0.5" />
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-1">Team Collaboration</h3>
                  <p className="text-sm text-gray-600">
                    Share insights, tag colleagues with @mentions, and collaborate on compliance records. 
                    All comments are timestamped and visible to your team.
                  </p>
                </div>
              </div>
            </div>
            <CommentThread recordType="compliance_record" recordId="demo-compliance-id" />
          </div>
        )}
      </div>
    </div>
  );
};

export default ComplianceDashboard;